import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DashboardTripRow } from '../../types/dashboard';

interface Props {
  trips: DashboardTripRow[];
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const colors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-blue-100 text-blue-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100'}`}>
      {t(`trips.statuses.${status}`) || status}
    </span>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center space-x-4 py-3 border-b">
        <div className="h-4 w-12 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
        <div className="h-4 w-16 bg-gray-200 rounded"></div>
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

export const TripQuickView: React.FC<Props> = ({ trips, loading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRowClick = (tripId: number) => {
    navigate(`/trips/${tripId}`);
  };

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold mb-4">{t('dashboard.recentTrips')}</h3>

      {loading ? (
        <LoadingSkeleton />
      ) : trips.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-500">
          {t('common.noData')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr className="text-left text-gray-500">
                  <th className="pb-2 font-medium">ID</th>
                  <th className="pb-2 font-medium">{t('trips.date')}</th>
                  <th className="pb-2 font-medium">{t('trips.vehicle')}</th>
                  <th className="pb-2 font-medium">{t('trips.customer')}</th>
                  <th className="pb-2 font-medium text-right">{t('dashboard.revenue')}</th>
                  {/* <th className="pb-2 font-medium text-right">{t('dashboard.profit')}</th> */}
                  <th className="pb-2 font-medium text-center">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip) => (
                  <tr
                    key={trip.tripId}
                    onClick={() => handleRowClick(trip.tripId)}
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="py-3 text-primary-600 font-medium">#{trip.tripId}</td>
                    <td className="py-3">{format(parseISO(trip.date), 'dd/MM/yyyy')}</td>
                    <td className="py-3">{trip.vehicle}</td>
                    <td className="py-3 max-w-32 truncate">{trip.customer}</td>
                    <td className="py-3 text-right">{formatCurrency(trip.revenue)}đ</td>
                    {/* <td
                      className={`py-3 text-right font-medium ${
                        trip.profit >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {formatCurrency(trip.profit)}đ
                      <span className="text-xs text-gray-400 ml-1">
                        ({trip.profitMargin.toFixed(0)}%)
                      </span>
                    </td> */}
                    <td className="py-3 text-center">
                      <StatusBadge status={trip.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
